import { createReducer } from '@reduxjs/toolkit';
import { switchAccountSuccess } from 'store/account/actions';
import { oc } from 'ts-optchain';
import { setTriageSession } from 'storage/triageSessions/storage';
import * as actions from '../actions';

interface TriageGyantSessionTokenData {
  sessionToken: string;
  userId: string;
}

export interface TriageGyantSessionTokenState {
  isFetching: boolean;
  error: null | Error;
  data: TriageGyantSessionTokenData | null;
  hasFetchedLastSession: boolean;
  hasTimedOut: boolean;
  isFetchingLastSession: boolean;
  oldSessions: string[];
  resetChat: boolean;
}

export const triageGyantSessionTokenInitialState: TriageGyantSessionTokenState = {
  isFetching: false,
  error: null,
  data: null,
  hasFetchedLastSession: false,
  hasTimedOut: false,
  isFetchingLastSession: false,
  oldSessions: [],
  resetChat: false
};

const triageGyantSessionTokenReducer = createReducer(triageGyantSessionTokenInitialState, {
  [actions.TRIAGE_GYANT_SESSION_TOKEN]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.TRIAGE_GYANT_LAST_SESSION_TOKEN]: state => {
    state.isFetchingLastSession = true;
    state.hasFetchedLastSession = false;
  },
  [actions.TRIAGE_GYANT_SESSION_TOKEN_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = oc(action).payload.data('');
    // Set the last item so we can find it in the list that is sent back.
    setTriageSession(action.payload.data.sessionToken);
  },
  [actions.TRIAGE_GYANT_LAST_SESSION_TOKEN_SUCCESS]: (state, action) => {
    state.isFetchingLastSession = false;
    state.hasFetchedLastSession = true;
    state.oldSessions = action.payload.data?.sessionTokens ?? [];
  },

  [actions.TRIAGE_GYANT_SESSION_TOKEN_FAIL]: (state, action) => {
    state.isFetching = false;
    state.data = '';
    // Handling for network and firewall issues.
    state.error =
      action.error.message === 'Network Error'
        ? new Error(
            'Symptom Checker Error: You are having network issues or are behind a firewall. Symptom checker will not work behind a firewall.'
          )
        : action.error;
  },
  [actions.TRIAGE_GYANT_LAST_SESSION_TOKEN_FAIL]: (state, action) => {
    // 'Unknown patient ID' from last session simply means they have not done a session before.
    const errors = action.error.response?.data?.errors;
    if (Array.isArray(errors) && errors.includes('Unknown patient ID')) {
      // Data does not exist on the server
      state.isFetchingLastSession = false;
      state.hasFetchedLastSession = true;
      state.oldSessions = [];
      state.error = null;
    } else {
      state.isFetchingLastSession = false;
      // Handling for network and firewall issues.
      state.error =
        action.error.message === 'Network Error'
          ? new Error(
              'Symptom Checker Error: You are having network issues or are behind a firewall. Symptom checker will not work behind a firewall.'
            )
          : action.error;
    }
  },
  [actions.RESET_TRIAGE_CHAT_DATA]: state => {
    state.data = null;
    state.oldSessions = [];
    state.hasFetchedLastSession = false;
    state.error = null;
  },
  [actions.RESET_TRIAGE_CHAT_BOOLEAN_SWITCH]: state => {
    state.resetChat = true;
  },
  [actions.UNSET_TRIAGE_CHAT_BOOLEAN_SWITCH]: state => {
    state.resetChat = false;
  },
  [switchAccountSuccess.type]: state => {
    return { ...triageGyantSessionTokenInitialState, hasTimedOut: !state.hasTimedOut };
  }
});

export default triageGyantSessionTokenReducer;
